import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, SaveButton, Section } from "@/components/CalcUI";
import { useUnits, L } from "@/context/UnitsContext";
import { useUnitInputs, useOnUnitsChange } from "@/lib/units";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";

export default function Hydrostatic() {
  const { units } = useUnits();
  const u = L(units);
  const [inp, setInp] = useUnitInputs({ mw: "10.0", tvd: "10000" }, { mw: "density", tvd: "length" });
  const [out, setOut] = useState(null);
  useOnUnitsChange(() => setOut(null));

  const [fsInp, setFsInp] = useState({ lot_surface: "1200", mw: "10.0", shoe_tvd: "4500" });
  const [fsOut, setFsOut] = useState(null);

  const run = async () => {
    try {
      const r = await runCalc("hydrostatic_pressure", { mw: parseFloat(inp.mw), tvd: parseFloat(inp.tvd) }, units);
      setOut(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };
  const runFs = async () => {
    try {
      const r = await runCalc("formation_strength", {
        lot_surface: parseFloat(fsInp.lot_surface),
        mw: parseFloat(fsInp.mw),
        shoe_tvd: parseFloat(fsInp.shoe_tvd),
      });
      setFsOut(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="hydrostatic-page">
      <Section title="Hydrostatic" subtitle="Static column pressure from mud weight and TVD. Use TVD, never MD.">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard
            testid="hp-card"
            title="Hydrostatic Pressure"
            formula={units === "field" ? "HP = MW·0.052·TVD" : "HP = MW·9.81·TVD·1e-5"}
          >
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="hp-mw" label="Mud Weight" unit={u.density} value={inp.mw} onChange={(v) => setInp({ ...inp, mw: v })} />
              <NumField testid="hp-tvd" label="TVD" unit={u.length} value={inp.tvd} onChange={(v) => setInp({ ...inp, tvd: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="hp-run" onClick={run}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => out && { category: "Hydrostatic", calculator: "hydrostatic_pressure", inputs: inp, outputs: out, units, default_name: "Hydrostatic" }} />
            </div>
            {out && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Hydrostatic" value={out.pressure} unit={out.unit} emphasis />
              </div>
            )}
          </CalcCard>

          <CalcCard
            testid="fg-card"
            title="Formation Strength (from LOT)"
            formula="FG = (LOT_surf + MW·0.052·Shoe TVD)/(0.052·Shoe TVD)"
          >
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="fg-lot" label="LOT surface" unit="psi" value={fsInp.lot_surface} onChange={(v) => setFsInp({ ...fsInp, lot_surface: v })} />
              <NumField testid="fg-mw" label="Test MW" unit="ppg" value={fsInp.mw} onChange={(v) => setFsInp({ ...fsInp, mw: v })} />
              <NumField testid="fg-shoe" label="Shoe TVD" unit="ft" value={fsInp.shoe_tvd} onChange={(v) => setFsInp({ ...fsInp, shoe_tvd: v })} />
            </div>
            <div className="mt-4"><PrimaryButton testid="fg-run" onClick={runFs}>Calculate</PrimaryButton></div>
            {fsOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Formation gradient" value={fsOut.fg_ppg} unit="ppg" emphasis />
              </div>
            )}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
